import { UserProfile, FullUserProfile } from "./userProfileModel";
import { LoggingService } from "./loggingSvc";
import { FlightingService, FrameworkFlightingService } from "./flightingSvc";
import { UserTenantService } from "./user-tenant-service";
import { ApplicationContextService } from "./context/application-context-service";
import { HttpTrackingConfig } from "../../ShellCommon/logger/http-interceptor";
import { ServiceDeskConfig } from "../Startup/serviceDeskConfig";
import { Prerequisite } from "./loading/prerequisite-loading-service";
import { MsalAuthenticationServiceImplementation } from "./msal/msal-authentication-service";

("use strict");

/**
 * The service responsible for retrieving user profile
 */
export interface UserProfileService {
  /**
   * DEPRECATED: Use GetUserProfileSync
   * GetUserProfile returns userprofile for the currently logged on user.
   * @param {boolean} refresh optional paramater to refresh the userprofile pulled by first call.
   */
  GetUserProfile(refresh?: boolean): ng.IPromise<UserProfile>;

  /**
   * GetUserProfileSync returns userprofile for the currently logged on user.
   */
  GetUserProfileSync(): UserProfile;

  /**
   * GetAgentProfile returns userprofile for the agentPuid passed as parameter
   * @param {string} puid agentpuid to search for userprofile.
   */
  GetAgentProfile(puid: string): ng.IPromise<UserProfile>;

  /**
   * GetAgentProfile returns fullagentprofile along with basic user profile for the agentPuid passed as parameter
   * @param {string} puid agentpuid to search for userprofile.
   */
  GetFullAgentProfile(puid: string): ng.IPromise<FullUserProfile>;
}

export class UserProfileServiceImplementation implements UserProfileService {
  private static userProfile: UserProfile = undefined;

  // @ngInject
  constructor(
    private $http: ng.IHttpService,
    private msalAuthenticationService: MsalAuthenticationServiceImplementation,
    private $q: ng.IQService,
    private applicationContextService: ApplicationContextService,
    private loggingService: LoggingService,
    private flightingService: FlightingService & FrameworkFlightingService,
    private userTenantService: UserTenantService,
    private sdConfig: ServiceDeskConfig
  ) {}

  /**
   * This function is deprecated. It will always return undefine.
   */
  GetUserProfile(refresh?: boolean): ng.IPromise<UserProfile> {
    if (!refresh && UserProfileServiceImplementation.userProfile) {
      return this.$q.resolve(UserProfileServiceImplementation.userProfile);
    }

    return this.msalAuthenticationService._getCachedToken(this.sdConfig.msalScopes).then(function (token) {
      var cachedToken: string = "null";
      if (token) {
        cachedToken = token;
      }
      return this.$http
        .get(this.sdConfig.sdBaseUrl + "customer/api/UserProfile/GetUserProfile", <HttpTrackingConfig>{
          headers: {
            Authorization: "bearer " + cachedToken
          },
          dependencyOperationName: "ServiceDesk.GetUserProfile"
        })
        .then(
          (response: ng.IHttpPromiseCallbackArg<UserProfile>) => {
            UserProfileServiceImplementation.userProfile = response.data;
            if (UserProfileServiceImplementation.userProfile.AgentId) {
              this.applicationContextService.attachProfileDetails(UserProfileServiceImplementation.userProfile);
            } else {
              if (response.config.headers && response.config.headers["Authorization"]) {
                response.config.headers["Authorization"] = "<Redacted>";
              }

              this.loggingService.logError({
                message: "User Profile did not contain an agentID.",
                response: response,
                aadUser: this.msalAuthenticationService.userInfo
              });
              return <ng.IPromise<UserProfile>>this.$q.reject("No User Profile could be found for the given user.");
            }

            return UserProfileServiceImplementation.userProfile;
          },
          (response: ng.IHttpPromiseCallbackArg<any>) => {
            return this.$q.reject(response.data);
          }
        );
    });
  }

  /**
   * This function is deprecated. It will always return undefine.
   */
  GetUserProfileSync(): UserProfile {
    return UserProfileServiceImplementation.userProfile;
  }

  GetAgentProfile(puid: string): ng.IPromise<UserProfile> {
    if (!puid) {
      throw new Error("Please provide valid AgentId");
    }

    return this.msalAuthenticationService._getCachedToken(this.sdConfig.msalScopes).then(function (token) {
      var cachedToken: string = "null";
      if (token) {
        cachedToken = token;
      }
      return this.$http
        .get(this.sdConfig.sdBaseUrl + "SDController/api/ServiceDesk/GetAgentProfile/" + puid, <HttpTrackingConfig>{
          headers: {
            Authorization: "bearer " + cachedToken
          },
          dependencyOperationName: "ServiceDesk.GetApamProfile"
        })
        .then(
          (response: ng.IHttpPromiseCallbackArg<UserProfile>) => {
            return <UserProfile>response.data;
          },
          (response: ng.IHttpPromiseCallbackArg<{}>) => {
            return this.$q.reject(response.data);
          }
        );
    });
  }

  GetFullAgentProfile(agentAlias: string): ng.IPromise<FullUserProfile> {
    if (!agentAlias) {
      throw new Error("Please provide valid Agent alias");
    }

    return this.msalAuthenticationService._getCachedToken(this.sdConfig.msalScopes).then(function (token) {
      var cachedToken: string = "null";
      if (token) {
        cachedToken = token;
      }

      return this.$http
        .get(this.sdConfig.sdBaseUrl + "SDController/api/ServiceDesk/FullAgentProfile/" + agentAlias, <
          HttpTrackingConfig
        >{
          headers: {
            Authorization: "bearer " + cachedToken
          },
          dependencyOperationName: "ServiceDesk.GetFullAgentProfile"
        })
        .then(
          (response: ng.IHttpPromiseCallbackArg<FullUserProfile>) => {
            return <FullUserProfile>response.data;
          },
          (response: ng.IHttpPromiseCallbackArg<{}>) => {
            return this.$q.reject(response.data);
          }
        );
    });
  }

  public prerequisiteLoadCallback(prerequisite: Prerequisite): ng.IPromise<any> {
    if (!prerequisite.userProfile) {
      return this.$q.resolve();
    }
    if (
      !angular.isArray(prerequisite.userProfile) ||
      prerequisite.userProfile.find((bool) => {
        return typeof bool != "boolean";
      })
    ) {
      throw new Error(`malformed prerequisite for userProfile. ${prerequisite}`);
    }
    if (prerequisite.userProfile.includes(true)) {
      /**
       * HACK to unblock Agent Desktop scenario and Comm console since they will have a different method of getting agent profile.
       * One those methods have been added, this hack should be removed.
       */
      if (this.userTenantService.isUserOnLegacyAuthTenant()) {
        return this.loadUserProfile();
      } else {
        return this.flightingService.isFlightActive("AgentProfileEntity").then((isFlightEnabled: boolean) => {
          if (!isFlightEnabled) {
            return this.loadUserProfile();
          }
        });
      }
    }
    return this.$q.resolve();
  }

  private loadUserProfile(): ng.IPromise<UserProfile> {
    return this.GetUserProfile().catch((error) => {
      this.loggingService.logError(error);
      return this.$q.resolve(error);
    });
  }
}
